import { TrackContext } from './context';
import { arrowRotateCwIcon } from '@progress/kendo-svg-icons';
import { Grid, GridColumn as Column, GridDetailExpandChangeEvent, GridToolbar, GridNoRecords, GridCustomCellProps } from "@progress/kendo-react-grid";
import { fetchApi } from "core/fetchApi";
import { useCallback, useEffect, useState } from "react";
import { ILink } from "core/link";
import { useInterval } from "usehooks-ts";
import TrackDetailRow from "./DetailRow";
import ReferenceCell from "./ReferenceCell";
import PickupCell from "./PickupCell";
import DeliveryCell from "./DeliveryCell";
import StatusCell from "./StatusCell";
import AppTrackingCell from "./AppTrackingCell";
import NextStopCell from "./NextStopCell";
import ETACell from "./ETACell";
import PositionCell from "./PositionCell";
import DistanceCell from "./DistanceCell";
import PayCell from "./PayCell";
import { Button } from "@progress/kendo-react-buttons";
import Arrive from "./Milestones/Arrive";
import Load from "./Milestones/Load";
import Empty from "./Milestones/Empty";

export type ViewModel = {
  OrderID: number;
  OrderNumber: number;
  TripID: number;
  TripNumber: number;
  ReferenceNumber1: string;
  OrderStopStatus: number;
  OrderStopEvent: number;
  VehicleSpeed: number;
  PickupDateTime: string;
  PickupLocation: string;
  DeliveryDateTime: string;
  DeliveryLocation: string;
  Verb: string;
  PositionLocation: string;
  PositionRepeatCount: number;
  ScheduledDateTime: string;
  EarliestStopDateTime: string | null;
  LatestStopDateTime: string | null;
  PositionDateTime: string;
  ETADateTime: string | null;
  ETAMiles: number;
  MinutesLate: number;
  LoadedMiles: number;
  StopCount: number;
  StopSequence: number;
  RequiredSpeed: number;
  IsRunningLate: boolean;
  IsRunningLatePickup: boolean;
  IsRunningLateDelivery: boolean;
  IsScheduledSoon: boolean;
  TimeCriticalMinutes: number;
  IsCritical: boolean;
  NeedsCheckCall: boolean;
  WaitingAtArrivedMinutes: number;
  TotalPay: number;
  BilledVehicleTypeName: string;
  DriverAppCarrierHours: number | null;
  Links: ILink[];
  Hash: string;

  expanded?: boolean;
}

const Track = () => {

  const [data, setData] = useState<ViewModel[]>([]);
  const [arriveLink, setArriveLink] = useState<ILink | null>(null);
  const [loadLink, setLoadLink] = useState<ILink | null>(null);
  const [emptyLink, setEmptyLink] = useState<ILink | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchRecords = useCallback(() => {
    setLoading(true);
    fetchApi<{ Trips: Omit<ViewModel, 'expanded'>[] }>({ url: 'Grid', method: 'POST' })
      .then(({ Trips }) => {
        setData((prevState) => Trips.map(s => {
          const expanded = prevState.find(p => p.TripID === s.TripID)?.expanded;
          return { ...s, expanded };
        }));
        setLoading(false);
      }).catch(() => {
        setLoading(false);
        alert('Error fetching data.');
      });
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // refresh every 10 minutes
  useInterval(fetchRecords, 1000 * 60 * 10);

  const expandChange = (e: GridDetailExpandChangeEvent) => {
    setData(prevData => prevData.map(item => ({
      ...item, expanded: !!e.detailExpand[item.TripID]
    })));
  }

  const MilestoneButtonCell = (props: GridCustomCellProps) => {
    const { dataItem } = props;
    const arriveLink = dataItem.Links?.find((link: ILink) => link.Name === "GetArrive");
    const loadLink = dataItem.Links?.find((link: ILink) => link.Name === "GetLoad");
    const emptyLink = dataItem.Links?.find((link: ILink) => link.Name === "GetEmpty");
    return (
      <td {...props.tdProps}>
        {arriveLink && (
          <Button
            onClick={() => setArriveLink(arriveLink)}
            title="Arrive"
            themeColor="primary"
            fillMode="link"
          >
            Arrive
          </Button>
        )}
        {loadLink && (
          <Button
            onClick={() => setLoadLink(loadLink)}
            title="Load"
            themeColor="primary"
            fillMode="link"
          >
            Load
          </Button>
        )}
        {emptyLink && (
          <Button
            onClick={() => setEmptyLink(emptyLink)}
            title="Empty"
            themeColor="primary"
            fillMode="link"
          >
            Empty
          </Button>
        )}
      </td>
    );
  };

  return <TrackContext.Provider value={{ refresh: fetchRecords }}>
    {loading && <div className="k-loading-mask">
      <span className="k-loading-text">Loading</span>
      <div className="k-loading-image"></div>
      <div className="k-loading-color"></div>
    </div>}
    {arriveLink && <Arrive
      CloseDialog={() => setArriveLink(null)}
      Link={arriveLink} />}
    {loadLink && <Load
      CloseDialog={() => setLoadLink(null)}
      Link={loadLink} />}
    {emptyLink && <Empty
      CloseDialog={() => setEmptyLink(null)}
      Link={emptyLink} />}
    <Grid
      data={data}
      size="small"
      scrollable="none"
      dataItemKey="TripID"
      detail={TrackDetailRow}
      detailExpand={Object.fromEntries(data.map(item => [item.TripID, !!item.expanded]))}
      onDetailExpandChange={expandChange}
    >
      <GridToolbar>
        <Button
          svgIcon={arrowRotateCwIcon}
          title="Refresh"
          onClick={fetchRecords}
        />
      </GridToolbar>
      <GridNoRecords>
        No In-Progress Trips Found
      </GridNoRecords>
      <Column field="OrderNumber" title="Reference" filter="numeric" cells={{ data: ReferenceCell }} />
      <Column field="PickupDateTime" title="Pickup" filter="date" cells={{ data: PickupCell }} />
      <Column field="DeliveryDateTime" title="Delivery" filter="date" cells={{ data: DeliveryCell }} />
      <Column field="OrderStopStatus" title="Status" filter="numeric" cells={{ data: StatusCell }} />
      <Column title="Tracking" sortable={false} filterable={false} cells={{ data: AppTrackingCell }} />
      <Column field="ScheduledDateTime" title="Next Stop" cells={{ data: NextStopCell }} />
      <Column field="ETADateTime" title="ETA" filter="date" cells={{ data: ETACell }} />
      <Column field="PositionDateTime" title="Last Position" cells={{ data: PositionCell }} />
      <Column field="ETAMiles" title="Miles" headerClassName="align-right" cells={{ data: DistanceCell }}  />
      <Column field="TotalPay" title="Pay" headerClassName="align-right" cells={{ data: PayCell }}  />
      <Column title="Actions" cells={{ data: MilestoneButtonCell }} />
    </Grid>
  </TrackContext.Provider>;
}

export default Track;